import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';

import { extractFileName } from './streamUtils';

const drawerPaperProps = {
    sx: {
        width: { xs: '100%', md: '720px' },
    },
};

const sectionStyle = {
    marginBottom: '1.5rem',
};

const StreamEditDrawer = ({
    open,
    onClose,
    stream = null,
    typeOptions,
    translations = {},
    mediaFiles = [],
    fluxes = [],
    featureFlags = {},
    onChange,
    onRemove,
}) => {
    if (!stream) {
        return null;
    }

    const t = (key, fallback) => translations[key] || fallback;
    const tWith = (key, fallback, replacements = {}) => {
        const template = translations[key] || fallback;
        return Object.entries(replacements).reduce((acc, [needle, value]) => acc.replace(new RegExp(needle, 'g'), value), template);
    };

    const describeFlux = (flux, { includeDevice = false } = {}) => {
        if (!flux || typeof flux !== 'object') {
            return '';
        }

        const parts = [];
        const name = typeof flux?.name === 'string' ? flux.name.trim() : '';
        if (name) {
            parts.push(name);
        }

        if (typeof flux.id !== 'undefined' && flux.id !== null && `${flux.id}`.trim() !== '') {
            parts.push(tWith('fluxIdSuffix', 'Flux ID #:id', { ':id': flux.id }));
        }

        if (includeDevice && flux.device_id) {
            parts.push(tWith('fluxDeviceSuffix', 'Device #:id', { ':id': flux.device_id }));
        }

        return parts.join(' - ');
    };

    const mediaOptions = Array.isArray(mediaFiles) ? mediaFiles : [];
    const fluxesArray = Array.isArray(fluxes) ? fluxes : [];

    const {
        advancedFluxPicker = false,
        fluxPickerMaxHeightRatio = 0.5,
        filterFluxTransportOnly = false,
    } = featureFlags || {};

    const isFluxEligibleLocal = useCallback((flux) => {
        if (!filterFluxTransportOnly) {
            return true;
        }
        if (!flux || typeof flux !== 'object') {
            return false;
        }
        const value = flux.uploaded_link;
        if (typeof value === 'boolean') {
            return value === false;
        }
        if (value === null || typeof value === 'undefined') {
            return true;
        }
        const normalised = String(value).trim().toLowerCase();
        return normalised === '' || normalised === '0' || normalised === 'false' || normalised === 'no';
    }, [filterFluxTransportOnly]);

    const mapFluxToOption = useCallback((flux) => {
        if (!flux || typeof flux !== 'object') {
            return null;
        }

        const name = typeof flux.name === 'string' && flux.name.trim()
            ? flux.name.trim()
            : t('summaryFluxFallback', 'Flux stream');

        const groupLabel = typeof flux.group === 'string' && flux.group.trim()
            ? flux.group.trim()
            : '';

        const idValue = typeof flux.id !== 'undefined' && flux.id !== null ? flux.id : '';
        const url = flux.link || '';

        const labelParts = [];
        if (idValue !== '') {
            labelParts.push(`#${idValue}`);
        }
        if (groupLabel) {
            labelParts.push(groupLabel);
        }
        labelParts.push(name);       
        if (url) {
            labelParts.push(url);
        }

        const searchParts = [...labelParts];
        if (flux.device_id) {
            searchParts.push(String(flux.device_id));
        }

        return {
            value: String(idValue),
            id: idValue,
            name,
            group: groupLabel,
            url,
            label: labelParts.join(' - '),
            searchText: searchParts.join(' ').toLowerCase(),
        };
    }, [t]);

    const selectedFile = mediaOptions.find((file) => String(file.id) === String(stream.sourceIdentifier));
    const selectedFlux = fluxesArray.find((flux) => String(flux.id) === String(stream.sourceIdentifier));
    const selectedFluxOption = useMemo(() => mapFluxToOption(selectedFlux), [mapFluxToOption, selectedFlux]);

    const fluxOptionCollection = useMemo(() => {
        const optionMap = new Map();

        fluxesArray.forEach((flux) => {
            const identifier = String(flux?.id ?? '');
            if (!identifier) {
                return;
            }
            const isSelected = identifier === String(stream.sourceIdentifier);
            if (!isSelected && !isFluxEligibleLocal(flux)) {
                return;
            }
            const option = mapFluxToOption(flux);
            if (option) {
                optionMap.set(option.value, option);
            }
        });

        if (selectedFluxOption && selectedFluxOption.value) {
            optionMap.set(selectedFluxOption.value, selectedFluxOption);
        }

        return Array.from(optionMap.values());
    }, [fluxesArray, isFluxEligibleLocal, mapFluxToOption, selectedFluxOption, stream.sourceIdentifier]);

    const fluxFilter = useMemo(() => createFilterOptions({ stringify: (option) => option.searchText }), []);

    const listboxMaxHeight = useMemo(() => {
        const ratio = Number(fluxPickerMaxHeightRatio);
        const numeric = Number.isFinite(ratio) ? ratio : 0.5;
        const clamped = Math.min(Math.max(numeric, 0.2), 0.9);
        return `${Math.round(clamped * 100)}vh`;
    }, [fluxPickerMaxHeightRatio]);

    const resolvedUrl = (() => {
        if (stream.sourceType === 'uploaded') {
            return selectedFile?.path || '';
        }
        if (stream.sourceType === 'flux') {
            return selectedFlux?.link || stream?.url || '';
        }
        return stream?.url || '';
    })();

    const hasFluxOptions = fluxOptionCollection.length > 0;
    const autocompleteValue = selectedFluxOption || null;

    const resolvedFileName = stream.sourceType === 'uploaded'
        ? (selectedFile ? (selectedFile.name || selectedFile.upload_name || `#${selectedFile.id}`) : '')
        : stream.sourceType === 'file'
            ? ''
            : extractFileName(resolvedUrl);

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={drawerPaperProps}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('drawerTitle', 'Edit stream')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('drawerSubtitle', 'Adjust source and metadata for this tile.')}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label={t('close', 'Close')}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />

            <Box sx={{ px: 3, py: 3 }}>
                <Box sx={sectionStyle}>
                    <TextField
                        fullWidth
                        label={t('titleLabel', 'Title')}
                        placeholder={t('titlePlaceholder', 'Give this stream a name')}
                        value={stream.title}
                        onChange={(event) => onChange({ title: event.target.value })}
                    />
                </Box>

                <Box sx={sectionStyle}>
                    <TextField
                        fullWidth
                        select
                        label={t('streamType', 'Stream type')}
                        value={stream.sourceType}
                        onChange={(event) => onChange({ sourceType: event.target.value })}
                    >
                        {typeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                {stream.sourceType === 'rtsp' && (
                    <Box sx={sectionStyle}>
                        <TextField
                            fullWidth
                            label={t('urlLabel', 'Stream URL')}
                            placeholder={t('urlPlaceholder', 'rtsp:// or https://')}
                            value={stream.url}
                            onChange={(event) => onChange({ url: event.target.value })}
                        />
                    </Box>
                )}

                {stream.sourceType === 'file' && (
                    <Box sx={sectionStyle}>
                        <TextField
                            fullWidth
                            label={t('filePathLabel', 'File path')}
                            placeholder={t('filePathPlaceholder', '/opt/signage/streams/video.mp4')}
                            value={stream.url}
                            onChange={(event) => onChange({ url: event.target.value })}
                        />
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                            {t('summaryFileFallback', 'Local file path')}
                        </Typography>
                    </Box>
                )}

                {stream.sourceType === 'uploaded' && (
                    <Box sx={sectionStyle}>
                        {mediaOptions.length > 0 ? (
                            <TextField
                                fullWidth
                                select
                                label={t('uploaded', 'Uploaded media')}
                                value={stream.sourceIdentifier}
                                onChange={(event) => onChange({ sourceIdentifier: event.target.value })}
                            >
                                {mediaOptions.map((file) => (
                                    <MenuItem key={file.id} value={String(file.id)}>
                                        {file.name || file.upload_name || `#${file.id}`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <Typography variant="body2" color="warning.main">
                                {t('noMediaFiles', 'No uploaded media files available.')}
                            </Typography>
                        )}
                        {selectedFile?.path && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                {extractFileName(selectedFile.path)}
                            </Typography>
                        )}
                    </Box>
                )}

                {stream.sourceType === 'flux' && (
                    <Box sx={sectionStyle}>
                        {hasFluxOptions ? (
                            advancedFluxPicker ? (
                                <Autocomplete
                                    options={fluxOptionCollection}
                                    value={autocompleteValue}
                                    onChange={(event, newValue) => onChange({ sourceIdentifier: newValue ? newValue.value : '' })}
                                    getOptionLabel={(option) => option.label}
                                    filterOptions={fluxFilter}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    ListboxProps={{
                                        style: {
                                            maxHeight: listboxMaxHeight,
                                            overflowY: 'auto',
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            label={t('flux', 'Video flux')}
                                            placeholder={t('fluxSearchPlaceholder', 'Search streams')}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, width: '100%' }}>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                                                        {option.id !== '' ? (
                                                        <>
                                                            {`#${option.id}`} -
                                                        </>
                                                    ) : null} {option.name}
                                                    </Typography>
                                                    {option.group ? (
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {option.group}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                                <Box sx={{ textAlign: { sm: 'right' } }}>                                                    
                                                    <Typography variant="caption" color="text.disabled" sx={{ wordBreak: 'break-all' }}>
                                                        {option.url || t('summaryNoUrl', 'No stream URL yet')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </li>
                                    )}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    select
                                    label={t('flux', 'Video flux')}
                                    value={String(stream.sourceIdentifier ?? '')}
                                    onChange={(event) => onChange({ sourceIdentifier: event.target.value })}
                                >
                                    {fluxOptionCollection.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, width: '100%' }}>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                        {option.name}
                                                    </Typography>
                                                    {option.group ? (
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {option.group}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                                <Box sx={{ textAlign: { sm: 'right' } }}>
                                                    {option.id !== '' ? (
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {`#${option.id}`}
                                                        </Typography>
                                                    ) : null}
                                                    <Typography variant="caption" color="text.disabled" sx={{ wordBreak: 'break-all' }}>
                                                        {option.url || t('summaryNoUrl', 'No stream URL yet')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )
                        ) : (
                            <Typography variant="body2" color="warning.main">
                                {t('noFluxes', 'No flux streams available.')}
                            </Typography>
                        )}
                        {selectedFlux ? (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                {describeFlux(selectedFlux, { includeDevice: true }) || t('summaryFluxFallback', 'Flux stream')}
                            </Typography>
                        ) : null}
                        {(selectedFlux?.link) ? (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                {extractFileName(selectedFlux.link)}
                            </Typography>
                        ) : null}
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('summaryPreview', 'Current source preview')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {resolvedFileName || resolvedUrl || t('summaryNoUrl', 'No stream URL yet')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Button variant="outlined" color="error" onClick={onRemove}>
                        {t('remove', 'Remove')}
                    </Button>
                    <Button variant="contained" color="primary" onClick={onClose}>
                        {t('done', 'Done')}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

StreamEditDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    stream: PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.string,
        sourceType: PropTypes.string.isRequired,
        sourceIdentifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        url: PropTypes.string,
    }),
    typeOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
    })).isRequired,
    translations: PropTypes.object,
    mediaFiles: PropTypes.arrayOf(PropTypes.object),
    fluxes: PropTypes.arrayOf(PropTypes.object),
    featureFlags: PropTypes.shape({
        advancedFluxPicker: PropTypes.bool,
        fluxPickerMaxHeightRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        filterFluxTransportOnly: PropTypes.bool,
        allowUploadedMedia: PropTypes.bool,
    }),
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

export default StreamEditDrawer;
